import type { ArrayDelta } from "~/types/arrayDelta";

/**
 * Computes the difference between two arrays.
 * Returns which items were added and which were removed.
 *
 * - `added`: items that are present in `final` but not in `initial`
 * - `deleted`: items that are present in `initial` but not in `final`
 *
 * Note: comparison is based on strict equality (`===`).
 */
export function getArrayDelta(
  initial: unknown[],
  final: unknown[],
): ArrayDelta {
  return {
    // Items that exist in `final` but not in `initial`
    added: final.filter((item) => !initial.includes(item)),

    // Items that exist in `initial` but not in `final`
    deleted: initial.filter((aItem) => !final.includes(aItem)),
  };
}

/**
 * Checks whether all objects in an array share the same value
 * for a given key.
 *
 * @param arr - The array of objects to inspect
 * @param key - The property key to compare across all objects
 * @returns `true` if all items have the same value for `key`,
 *          or if the array has 0 or 1 item.
 */
export const allHaveSame = <T, K extends keyof T>(
  arr: T[],
  key: K,
): boolean => {
  // Trivially true for arrays of length 0 or 1
  if (arr.length <= 1) return true;

  // Reference value to compare against
  const first = arr[0][key];

  // Ensure every item has the same value
  return arr.every((item) => item[key] === first);
};
